import { FilterId, type PrimaryFilterId } from '../../../filters/types/filters';
import type { ChartType } from '../../PageConfigProvider/types/chart';
import {
  type SecondaryDimensionValues,
  secondaryDimensionOptions,
} from '../../PageConfigProvider/types/dimension';
import type { DimensionSource } from '../../PageConfigProvider/types/item';
import {
  isCompositionMetric,
  isPayMetric,
  type MetricValues,
} from '../../PageConfigProvider/types/metric';

export type DimensionSourceOption = {
  value: DimensionSource;
  label: string;
};

export const getAllDimensionSourceOptions = ({
  chartType,
  primaryFilterId,
  metric,
}: {
  chartType?: ChartType;
  primaryFilterId: PrimaryFilterId;
  metric?: MetricValues;
}): DimensionSourceOption[] => {
  if (chartType !== 'bar' && chartType !== 'stacked-bar') {
    return [];
  }

  const primaryDimensionLabel = (() => {
    switch (primaryFilterId) {
      case FilterId.COMPANY:
        return 'Company';
      case FilterId.GEOGRAPHY:
        return 'Geography';
      case FilterId.ROLE:
        return 'Role';
    }
  })();

  if (chartType === 'stacked-bar') {
    return [
      { value: 'primary', label: primaryDimensionLabel },
      { value: 'timeseries', label: 'Timeseries' },
    ];
  }

  const allowedDimensions = secondaryDimensionOptions
    .map((option) => option.value)
    .filter(filterSecondaryDimensionByPrimaryDimension(primaryFilterId))
    .filter(filterSecondaryDimensionsByMetric(metric));

  return [
    { value: 'primary', label: primaryDimensionLabel },
    ...allowedDimensions.map((dimension) => ({
      value: dimension,
      label: mapDimensionValueToLabel(dimension),
    })),
  ];
};

const mapDimensionValueToLabel = (
  dimension: SecondaryDimensionValues
): string => {
  switch (dimension) {
    case 'rics':
      return 'Top Industries';
    case 'company':
      return 'Top Companies';
    case 'geography':
      return 'Top Geographies';
    case 'role':
      return 'Top Roles';
    case 'skills':
      return 'Top Skills';
    case 'competitors':
      return 'Top Competitors';
    case 'education':
      return 'Education';
    case 'seniority':
      return 'Seniority';
    case 'gender':
      return 'Gender';
    case 'ethnicity':
      return 'Ethnicity';
    case 'timeseries':
      return 'Time';
  }
};

/** Label for the page's primary entity dimension (e.g. "Company"). */
export const getPrimaryDimensionLabel = (
  primaryFilterId: PrimaryFilterId | undefined
): string => {
  switch (primaryFilterId) {
    case FilterId.COMPANY:
      return 'Company';
    case FilterId.GEOGRAPHY:
      return 'Geography';
    case FilterId.ROLE:
      return 'Role';
    default:
      return 'Primary';
  }
};

/** Resolve a display label for any table dimension-column value. */
export const getDimensionLabel = (
  dimension: DimensionSource,
  primaryFilterId?: PrimaryFilterId
): string =>
  dimension === 'primary'
    ? getPrimaryDimensionLabel(primaryFilterId)
    : mapDimensionValueToLabel(dimension);

const filterSecondaryDimensionByPrimaryDimension =
  (primaryDimension: PrimaryFilterId) =>
  (secondaryDimension: SecondaryDimensionValues): boolean => {
    const allowedDimensions = ((): SecondaryDimensionValues[] => {
      switch (primaryDimension) {
        // COMPANY allowed alternate dimensions
        case FilterId.COMPANY:
          return [
            'geography',
            'role',
            'rics',
            'skills',
            'seniority',
            'education',
            'ethnicity',
            'gender',
            'competitors',
          ];
        // GEOGRAPHY allowed alternate dimensions
        case FilterId.GEOGRAPHY:
          return [
            'rics',
            'company',
            'role',
            'skills',
            'seniority',
            'education',
            'ethnicity',
            'gender',
          ];
        // ROLE allowed alternate dimensions
        case FilterId.ROLE:
          return [
            'rics',
            'geography',
            'skills',
            'seniority',
            'education',
            'ethnicity',
            'gender',
          ];
      }
    })();

    return allowedDimensions.includes(secondaryDimension);
  };

const filterSecondaryDimensionsByMetric =
  (metric: MetricValues | undefined) =>
  (secondaryDimension: SecondaryDimensionValues): boolean => {
    const allowedDimensions = (() => {
      if (isCompositionMetric(metric)) {
        // HEADCOUNT allowed alternate dimensions
        if (metric === 'headcount') {
          return [
            'rics',
            'company',
            'competitors',
            'geography',
            'role',
            'skills',
            'seniority',
            'education',
            'ethnicity',
            'gender',
          ];
        }
        // OTHER COMPOSITION METRICS allowed alternate dimensions
        return ['company', 'competitors', 'geography'];
      }
      if (isPayMetric(metric)) {
        // PAY METRICS allowed alternate dimensions
        return ['competitors'];
      }
      // OTHER Metrics currently do not support alternate dimensions
      return [];
    })();

    return allowedDimensions.includes(secondaryDimension);
  };
